import React, { useRef, useState } from 'react';
import HLPageTable, {
    HLPageTableFilterConfig,
    HLPageTableRowActionButton
} from "../../shared/components/table/HLPageTable";
import { Teacher, TeacherRole } from "../../shared/types/teacher";
import type { ColDef } from "ag-grid-community";
import { HLButtonStyleType } from "../../shared/components/button/HLButton";
import { StudentView } from "../../shared/types/student";
import { getAllStudentsFetch } from "../../app/axios/studentFetches";
import CreateStudentModal from '../../modal/student/CreateStudentModal';
import StudentsStatsModal from '../../modal/student/StudentsStatsModal';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from "../../shared/hooks/useAppSelector";
import { EnabledFilterOptions } from "../../shared/types/general";
import SwitchStudentModal from "../../modal/student/SwitchStudentModal";

const searchFiltersConfig: HLPageTableFilterConfig<StudentView>[] = [
    {
        fieldName: 'firstName',
        label: 'Имя',
        operator: 'like'
    },
    {
        fieldName: 'lastName',
        label: 'Фамилия',
        operator: 'like'
    },
    {
        fieldName: 'telegram',
        label: 'Telegram',
        operator: 'like'
    },
    {
        fieldName: 'email',
        label: 'Email',
        operator: 'like'
    },
    {
        fieldName: 'enabled',
        label: 'Активность',
        operator: 'is',
        type: 'select',
        options: EnabledFilterOptions,
        defaultValue: 'true'
    },
    {
        fieldName: 'registeredInBot',
        label: 'Бот',
        operator: 'is',
        type: 'select',
        options: [
            {
                value: 'true',
                label: 'Да'
            },
            {
                value: 'false',
                label: 'Нет'
            }
        ],
    }
];

const columnDefs: ColDef<StudentView>[] = [
    {
        field: 'firstName',
        headerName: 'Имя',
    },
    {
        field: 'lastName',
        headerName: 'Фамилия',
        valueFormatter: (params) => params.value ?? '-',
    },
    {
        field: 'telegram',
        headerName: 'Telegram',
        valueFormatter: (params) => params.value ?? '-',
    },
    {
        field: 'registeredInBot',
        headerName: 'Бот',
        valueFormatter: params => params.value ? 'Да' : 'Нет'
    },
    {
        field: 'email',
        headerName: 'Email',
        valueFormatter: (params) => params.value ?? '-',
    }
]

const defaultColDef: ColDef<Teacher> = {
    sortable: false,
    filter: false,
    resizable: true,
    flex: 1,
    minWidth: 100
}


const StudentsPage = () => {
    const currentTeacher = useAppSelector(state => state.auth.currentTeacher)

    const tableRef = useRef<any>(null);

    const navigate = useNavigate();
    const [isCreateStudentModalOpen, setIsCreateStudentModalOpen] = useState(false);
    const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
    const [isSwitchStudentModalOpen, setIsSwitchStudentModalOpen] = useState(false);
    const [student, setStudent] = useState<StudentView | null>(null);

    const handleSuccessCreate = (id: number) => navigate(`/students/${id}`);
    const handleCloseSwitch = () => {
        setIsSwitchStudentModalOpen(false)
        setStudent(null)
        tableRef.current?.refreshData()
    }

    const actionButtonsConfig = [];
    const rowActionButtons: HLPageTableRowActionButton[] = [];

    const handleOpenStudent = (params: { data?: StudentView }) => {
        if (params?.data?.id) window.open(`/students/${params.data.id}`, '_blank', 'noreferrer');
    };

    if (currentTeacher?.role === TeacherRole.ADMIN) {
        rowActionButtons.push({
            onClick: async params => {
                if (!params?.data) return
                setStudent(params.data)
                setIsSwitchStudentModalOpen(true)
            },
            getLabel: params => params?.data?.enabled ? 'Деактивировать' : 'Активировать'
        })

        actionButtonsConfig.push({
            label: 'Создать ученика',
            onClick: () => setIsCreateStudentModalOpen(true),
            styleType: HLButtonStyleType.ORANGE
        });

        actionButtonsConfig.push({
            label: 'Статистика',
            onClick: () => setIsStatsModalOpen(true),
            styleType: HLButtonStyleType.GRAY
        });
    }

    return (
        <>
            <HLPageTable
                ref={tableRef}
                title="Ученики"
                columns={columnDefs}
                defaultColDef={defaultColDef}
                filterConfig={searchFiltersConfig}
                actionButtons={actionButtonsConfig}
                fetchData={getAllStudentsFetch}
                rowActionButtons={rowActionButtons}
                onRowDoubleClick={handleOpenStudent}
                height={window.innerHeight}
            />
            <CreateStudentModal
                open={isCreateStudentModalOpen}
                setOpen={setIsCreateStudentModalOpen}
                onSuccess={handleSuccessCreate}
            />
            <StudentsStatsModal
                open={isStatsModalOpen}
                setOpen={setIsStatsModalOpen}
            />
            <SwitchStudentModal
                open={isSwitchStudentModalOpen}
                student={student}
                onClose={handleCloseSwitch}
            />
        </>
    );
};

export default StudentsPage;